/**
 * Exercise pop-up types.
 * Mirrors backend models.py ExercisePopupsSettings / PopupTypeStats / PopupRecentEvent
 * and the RTDB nodes devices/{id}/config/exercisePopups (settings, backend-written) and
 * devices/{id}/games/popupStats (statistics, written by the tablet) — see
 * contracts/firebase-schema.md §2.5 and §10.1.
 */

export interface ExercisePopupsSettings {
  enabled: boolean;
  intervalMinutes: number; // 1-720, screen-on minutes between pop-ups
  exercisesPerPopup: number; // 1-10
  exerciseTypes: string[]; // content exercise-type ids; [] = every type
  updatedAt?: number | null;
  updatedBy?: string | null;
}

export interface ExercisePopupsSettingsUpdate {
  enabled?: boolean;
  intervalMinutes?: number;
  exercisesPerPopup?: number;
  exerciseTypes?: string[]; // replaces the whole list; [] = every type
}

export const DEFAULT_EXERCISE_POPUPS_SETTINGS: ExercisePopupsSettings = {
  enabled: false,
  intervalMinutes: 30,
  exercisesPerPopup: 1,
  exerciseTypes: [],
};

export const INTERVAL_MINUTES_MIN = 1;
export const INTERVAL_MINUTES_MAX = 720;
export const EXERCISES_PER_POPUP_MIN = 1;
export const EXERCISES_PER_POPUP_MAX = 10;

/** One active exercise type of the shared content catalog (content/exerciseTypes) */
export interface ExerciseTypeInfo {
  id: string;
  title: Record<string, string>; // localized, e.g. { hu: 'Betűkereső', en: 'Letters' }
  order: number;
  iconUrl?: string | null;
}

/** Per-type counters, lifetime (totals) or for one day (daily) */
export interface PopupTypeStats {
  type_id: string;
  presented: number;
  solved: number;
  interrupted: number;
  correct: number; // rounds answered correctly
  wrong: number; // wrong taps before the right answer, summed
  first_try_correct: number; // rounds solved without any wrong tap
  solve_ms_total: number; // summed over solved exercises
  last_presented_at?: number | null;
  last_solved_at?: number | null;
}

export interface PopupDailyTypeStats extends PopupTypeStats {
  date: string; // YYYY-MM-DD (tablet local date)
}

export type PopupOutcome = 'solved' | 'interrupted';

export interface PopupRecentEvent {
  id: string;
  type_id: string;
  exercise_id: string;
  presented_at: number;
  finished_at: number;
  outcome: PopupOutcome;
  correct: number;
  wrong: number;
  first_try_correct: number;
  solve_ms: number;
}

export interface ExercisePopupStats {
  totals: PopupTypeStats[];
  daily: PopupDailyTypeStats[];
  recent: PopupRecentEvent[];
}

export const EMPTY_EXERCISE_POPUP_STATS: ExercisePopupStats = {
  totals: [],
  daily: [],
  recent: [],
};

/** Resolve a localized content title: UI language → hu → first available → the id. */
export function resolveTypeTitle(
  type: ExerciseTypeInfo | undefined,
  language: string,
  fallbackId: string
): string {
  if (!type) return fallbackId;
  const lang = language.split('-')[0];
  return (
    type.title[lang] ||
    type.title.hu ||
    Object.values(type.title)[0] ||
    fallbackId
  );
}
